import React, { useCallback, useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Spinner, Tooltip } from "react-bootstrap";
import { useHistory } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaBox,
  FaCheckCircle,
  FaClock,
  FaCog,
  FaEye,
  FaHandshake,
  FaMapMarkerAlt,
  FaPlus,
  FaSearch,
  FaSyncAlt,
  FaTruck,
  FaUsers,
} from "react-icons/fa";

import { getTokenFromLocalStorage } from "../../utilities/tokenUtils";
import { usePermissions } from "../../utilities/usePermissions";
import "./PartnerList.css";
import AppSelect from "../../components/ui/AppSelect";

const API_URL = process.env.REACT_APP_SERVER_URL || "";

const getRiderCount = (partner) =>
  partner.activeRiderCount ?? partner.riderCount ?? 0;

const PartnerList = () => {
  const history = useHistory();
  const { can, loaded: permissionsLoaded } = usePermissions();
  const canManagePartners =
    permissionsLoaded && can("three_pl:manage");
  const canCreatePartners =
    canManagePartners || (permissionsLoaded && can("three_pl:create"));
  const canUpdatePartners =
    canManagePartners || (permissionsLoaded && can("three_pl:update"));
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [updatingPartnerId, setUpdatingPartnerId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loadError, setLoadError] = useState("");

  const fetchPartners = useCallback(async (isRefresh = false) => {
    try {
      setLoadError("");
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const token = getTokenFromLocalStorage();
      const response = await axios.get(`${API_URL}/api/3pl-partners`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPartners(response.data.data?.partners || []);
    } catch (error) {
      console.error("Error fetching partners:", error);
      setLoadError("The partner directory could not be loaded.");
      toast.error(
        error.response?.data?.error?.message || "Failed to load 3PL partners."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  const updatePartnerStatus = async (partnerId, newStatus) => {
    if (!canUpdatePartners) return;
    const currentPartner = partners.find((partner) => partner._id === partnerId);
    const previousStatus = currentPartner?.status;

    try {
      setUpdatingPartnerId(partnerId);
      setPartners((currentPartners) =>
        currentPartners.map((partner) =>
          partner._id === partnerId
            ? { ...partner, status: newStatus }
            : partner
        )
      );

      const token = getTokenFromLocalStorage();
      await axios.put(
        `${API_URL}/api/3pl-partners/${partnerId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Partner status changed to ${newStatus}.`);
    } catch (error) {
      setPartners((currentPartners) =>
        currentPartners.map((partner) =>
          partner._id === partnerId
            ? { ...partner, status: previousStatus }
            : partner
        )
      );
      console.error("Error updating status:", error);
      toast.error(
        error.response?.data?.error?.message || "Failed to update partner status."
      );
    } finally {
      setUpdatingPartnerId(null);
    }
  };

  const metrics = useMemo(
    () => ({
      active: partners.filter((partner) => partner.status === "active").length,
      pending: partners.filter((partner) => partner.status === "pending").length,
      riders: partners.reduce(
        (total, partner) => total + getRiderCount(partner),
        0
      ),
    }),
    [partners]
  );

  const filteredPartners = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();

    return partners.filter((partner) => {
      const serviceAreas = (partner.serviceAreas || [])
        .map((area) => area?.name || area || "")
        .join(" ");
      const matchesSearch =
        !query ||
        partner.companyName?.toLowerCase().includes(query) ||
        String(partner.registrationNumber || "").toLowerCase().includes(query) ||
        partner.contactPerson?.name?.toLowerCase().includes(query) ||
        String(partner.contactPerson?.phone || "").toLowerCase().includes(query) ||
        partner.contactPerson?.email?.toLowerCase().includes(query) ||
        serviceAreas.toLowerCase().includes(query);
      const matchesStatus =
        statusFilter === "all" || partner.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [partners, searchTerm, statusFilter]);

  const hasFilters = Boolean(searchTerm) || statusFilter !== "all";

  return (
    <main className="partners-workspace">
      <header className="partners-workspace__header">
        <div className="partners-workspace__title">
          <span className="partners-workspace__eyebrow">Delivery network</span>
          <h1>
            <span aria-hidden="true">
              <FaHandshake />
            </span>
            3PL Partners
          </h1>
          <p>Manage logistics providers, fleet capacity, and service coverage.</p>
        </div>
        <div className="partners-workspace__actions">
          <button
            type="button"
            className="partners-button partners-button--ghost"
            onClick={() => history.push("/admin/riders-table")}
          >
            <FaTruck /> Rider fleet
          </button>
          <button
            type="button"
            className="partners-button partners-button--primary"
            onClick={() => history.push("/admin/3pl-partners-create")}
            disabled={!canCreatePartners}
          >
            <FaPlus /> Add partner
          </button>
        </div>
      </header>

      <section className="partners-metrics" aria-label="Partner network summary">
        <article className="partners-metric partners-metric--blue">
          <div>
            <span>Total partners</span>
            <strong>{partners.length}</strong>
          </div>
          <span className="partners-metric__icon">
            <FaHandshake />
          </span>
        </article>
        <article className="partners-metric partners-metric--green">
          <div>
            <span>Active partners</span>
            <strong>{metrics.active}</strong>
          </div>
          <span className="partners-metric__icon">
            <FaCheckCircle />
          </span>
        </article>
        <article className="partners-metric partners-metric--gold">
          <div>
            <span>Pending review</span>
            <strong>{metrics.pending}</strong>
          </div>
          <span className="partners-metric__icon">
            <FaClock />
          </span>
        </article>
        <article className="partners-metric partners-metric--coral">
          <div>
            <span>Active riders</span>
            <strong>{metrics.riders}</strong>
          </div>
          <span className="partners-metric__icon">
            <FaUsers />
          </span>
        </article>
      </section>

      <section className="partners-panel">
        <div className="partners-panel__heading">
          <div>
            <span>Partner directory</span>
            <strong>
              {filteredPartners.length} of {partners.length} partners shown
            </strong>
          </div>
          <button
            type="button"
            className="partners-refresh-button"
            onClick={() => fetchPartners(true)}
            disabled={refreshing}
          >
            <FaSyncAlt className={refreshing ? "is-spinning" : ""} />
            {refreshing ? "Refreshing" : "Refresh"}
          </button>
        </div>

        <div className="partners-toolbar">
          <label className="partners-search">
            <FaSearch aria-hidden="true" />
            <span className="sr-only">Search logistics partners</span>
            <input
              type="search"
              placeholder="Search company, contact, registration, or area"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </label>
          <label className="partners-status-filter">
            <span>Status</span>
            <AppSelect
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="suspended">Suspended</option>
              <option value="inactive">Inactive</option>
            </AppSelect>
          </label>
          {hasFilters && (
            <button
              type="button"
              className="partners-clear-button"
              onClick={() => {
                setSearchTerm("");
                setStatusFilter("all");
              }}
            >
              Clear filters
            </button>
          )}
        </div>

        {loading ? (
          <div className="partners-state" role="status">
            <Spinner animation="border" size="sm" />
            <strong>Loading partner network</strong>
            <span>Retrieving providers and fleet capacity.</span>
          </div>
        ) : loadError ? (
          <div className="partners-state partners-state--error">
            <FaSyncAlt />
            <strong>{loadError}</strong>
            <button
              type="button"
              className="partners-button partners-button--ghost"
              onClick={() => fetchPartners()}
            >
              Try again
            </button>
          </div>
        ) : (
          <div className="partners-table-scroll">
            <table className="partners-table">
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Primary contact</th>
                  <th>Service coverage</th>
                  <th>Fleet</th>
                  <th>Orders</th>
                  <th>Tier</th>
                  <th>Status</th>
                  <th className="partners-table__actions-heading">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredPartners.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="partners-state">
                        <FaHandshake />
                        <strong>
                          {hasFilters
                            ? "No partners match these filters"
                            : "No logistics partners yet"}
                        </strong>
                        <span>
                          {hasFilters
                            ? "Clear the filters to return to the full network."
                            : "Add a partner to begin building the delivery network."}
                        </span>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredPartners.map((partner) => {
                    const tier = partner.partnershipTier || "silver";
                    const status = partner.status || "inactive";
                    const serviceAreas = partner.serviceAreas || [];
                    const partnerInitial =
                      partner.companyName?.charAt(0).toUpperCase() || "P";

                    return (
                      <tr key={partner._id}>
                        <td>
                          <div className="partner-identity">
                            {partner.logo?.url ? (
                              <img
                                src={partner.logo.url}
                                alt=""
                                onError={(event) => {
                                  event.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              <span className="partner-logo-fallback">
                                {partnerInitial}
                              </span>
                            )}
                            <div>
                              <strong>
                                {partner.companyName || "Unnamed partner"}
                              </strong>
                              <span>
                                {partner.registrationNumber ||
                                  "No registration number"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="partner-contact">
                            <strong>
                              {partner.contactPerson?.name || "Not assigned"}
                            </strong>
                            <span>
                              {partner.contactPerson?.phone ||
                                partner.contactPerson?.email ||
                                "No contact details"}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className="partner-areas">
                            {serviceAreas.length ? (
                              <>
                                {serviceAreas.slice(0, 2).map((area, index) => (
                                  <span
                                    key={
                                      area?._id ||
                                      `${area?.name || area || "area"}-${index}`
                                    }
                                  >
                                    <FaMapMarkerAlt />
                                    {area?.name || area || "Unnamed area"}
                                  </span>
                                ))}
                                {serviceAreas.length > 2 && (
                                  <span className="partner-areas__more">
                                    +{serviceAreas.length - 2} more
                                  </span>
                                )}
                              </>
                            ) : (
                              <span className="partner-areas__empty">
                                Coverage not set
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="partner-volume">
                            <FaUsers />
                            <div>
                              <strong>{getRiderCount(partner)}</strong>
                              <span>active riders</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="partner-volume">
                            <FaBox />
                            <div>
                              <strong>{partner.orderCount ?? 0}</strong>
                              <span>total orders</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span
                            className={`partner-tier partner-tier--${tier.toLowerCase()}`}
                          >
                            {tier}
                          </span>
                        </td>
                        <td>
                          <div className="partner-status-control">
                            <span
                              className={`partner-status-dot partner-status-dot--${status}`}
                            />
                            <AppSelect
                              value={status}
                              onChange={(event) =>
                                updatePartnerStatus(
                                  partner._id,
                                  event.target.value
                                )
                              }
                              disabled={
                                !canUpdatePartners ||
                                updatingPartnerId === partner._id
                              }
                              aria-label={`Status for ${
                                partner.companyName || "partner"
                              }`}
                            >
                              <option value="pending">Pending</option>
                              <option value="active">Active</option>
                              <option value="suspended">Suspended</option>
                              <option value="inactive">Inactive</option>
                            </AppSelect>
                            {updatingPartnerId === partner._id && (
                              <Spinner animation="border" size="sm" />
                            )}
                          </div>
                        </td>
                        <td>
                          <div className="partners-row-actions">
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`view-${partner._id}`}>
                                  View partner
                                </Tooltip>
                              }
                            >
                              <button
                                type="button"
                                className="partners-icon-button"
                                onClick={() =>
                                  history.push(
                                    `/admin/3pl-partner-details/${partner._id}`
                                  )
                                }
                                aria-label={`View ${
                                  partner.companyName || "partner"
                                }`}
                              >
                                <FaEye />
                              </button>
                            </OverlayTrigger>
                            <OverlayTrigger
                              placement="top"
                              overlay={
                                <Tooltip id={`edit-${partner._id}`}>
                                  Edit partner
                                </Tooltip>
                              }
                            >
                              <button
                                type="button"
                                className="partners-icon-button partners-icon-button--settings"
                                onClick={() =>
                                  history.push(
                                    `/admin/3pl-partners-edit/${partner._id}`
                                  )
                                }
                                aria-label={`Edit ${
                                  partner.companyName || "partner"
                                }`}
                                disabled={!canUpdatePartners}
                              >
                                <FaCog />
                              </button>
                            </OverlayTrigger>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </main>
  );
};

export default PartnerList;
